import { DocumentLead, DocumentProvenance, PartFinderReport } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  ArchitectureOutlined,
  DescriptionOutlined,
  FactCheckOutlined,
  InsertDriveFileOutlined,
  MenuBookOutlined,
  SvgIconComponent,
} from '@mui/icons-material';
import { Box, Chip, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGetDocumentPreview } from '@/hooks/api/part-finder-reports/useGetDocumentPreview';
import { useObjectUrl } from '@/hooks/useObjectUrl';
import { useEffect, useState } from 'react';
import { CollapsibleSection } from './CollapsibleSection';
import { DocumentActionLinks, sourceHost } from './DocumentActionLinks';
import { DocumentPreviewLightbox, PreviewableDocument } from './DocumentPreviewLightbox';

const EMPTY_STATE = 'No corroborating documents found for this part yet.';
const PUBLIC_DOMAIN_LABEL = 'Public domain · gov';
const PUBLIC_DOMAIN_TOOLTIP = 'Published by DoD ASSIST under Distribution Statement A — approved for public release.';
const CONTENT_VERIFIED_LABEL = 'Content verified';

function evidenceQuoteLabel(lead: DocumentLead): string {
  return lead.evidenceQuoteVerified ? 'Verified in document' : 'Reported by search';
}

function contentVerifiedTooltip(matchedPartNumber: string): string {
  return `We read this document's text and found ${matchedPartNumber} in it.`;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  datasheet: 'Datasheet',
  drawing: 'Drawing',
  spec: 'Specification',
  manual: 'Manual',
};

const DOC_TYPE_ICONS: Record<string, SvgIconComponent> = {
  datasheet: DescriptionOutlined,
  drawing: ArchitectureOutlined,
  spec: FactCheckOutlined,
  manual: MenuBookOutlined,
};

const LeadCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '10px 12px',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.action.hover,
}));

function documentTypeLabel(documentType: string): string {
  return DOC_TYPE_LABELS[documentType] ?? 'Document';
}

const DocumentTypeIcon = ({ docType }: { docType: string }) => {
  const Icon = DOC_TYPE_ICONS[docType] ?? InsertDriveFileOutlined;
  return <Icon sx={{ fontSize: '1rem', color: 'text.secondary' }} />;
};

const PREVIEW_WIDTH = 96;
const PREVIEW_HEIGHT = 124;

const PreviewFrame = styled(Box)(({ theme }) => ({
  alignSelf: 'flex-start',
  flexShrink: 0,
  width: PREVIEW_WIDTH,
  height: PREVIEW_HEIGHT,
  borderRadius: '4px',
  overflow: 'hidden',
  cursor: 'pointer',
  '&:hover': { outline: `2px solid ${theme.palette.primary.main}` },
}));

const DocumentPreviewTile = ({
  reportId,
  index,
  title,
  onClick,
}: {
  reportId: string;
  index: number;
  title: string;
  onClick: () => void;
}) => {
  const { preview } = useGetDocumentPreview({ reportId, index, size: 'card' });
  const previewUrl = useObjectUrl(preview);
  if (!previewUrl) {
    return <></>;
  }
  return (
    <PreviewFrame onClick={onClick} role='button' aria-label={`Preview ${title}`} title={title}>
      <img
        src={previewUrl}
        alt={`First page of ${title}`}
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
      />
    </PreviewFrame>
  );
};

const DocumentLeadCard = ({
  lead,
  reportId,
  index,
  onPreviewClick,
}: {
  lead: DocumentLead;
  reportId: string;
  index: number;
  onPreviewClick: () => void;
}) => (
  <LeadCard sx={{ flexDirection: 'row', gap: '12px' }}>
    {lead.hasPreview && (
      <DocumentPreviewTile reportId={reportId} index={index} title={lead.title} onClick={onPreviewClick} />
    )}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <DocumentTypeIcon docType={lead.docType} />
        <BodyText
          title={lead.title}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
        >
          {documentTypeLabel(lead.docType)} · {lead.title}
        </BodyText>
        {lead.provenance === DocumentProvenance.PublicDomain && (
          <Tooltip title={PUBLIC_DOMAIN_TOOLTIP} describeChild>
            <Chip label={PUBLIC_DOMAIN_LABEL} size='small' color='success' variant='outlined' sx={{ flexShrink: 0 }} />
          </Tooltip>
        )}
        {lead.provenance === DocumentProvenance.ContentVerified && (
          <Tooltip title={contentVerifiedTooltip(lead.matchedPartNumber)} describeChild>
            <Chip
              label={CONTENT_VERIFIED_LABEL}
              size='small'
              color='success'
              variant='outlined'
              sx={{ flexShrink: 0 }}
            />
          </Tooltip>
        )}
      </Box>
      {lead.supersededBy && <BodyText color='text.secondary'>⤷ Superseded by {lead.supersededBy}</BodyText>}
      {lead.evidenceQuote && (
        <BodyText
          color='text.secondary'
          title={lead.evidenceQuote}
          sx={{
            fontStyle: 'italic',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {evidenceQuoteLabel(lead)}: &ldquo;{lead.evidenceQuote}&rdquo;
        </BodyText>
      )}
      <BodyText color='text.secondary'>
        {sourceHost(lead.url)} · matched {lead.matchedPartNumber}
      </BodyText>
      {lead.distributionStatement && <BodyText color='text.secondary'>{lead.distributionStatement}</BodyText>}
      {lead.section && <BodyText color='text.secondary'>{lead.section}</BodyText>}
      <DocumentActionLinks lead={lead} reportId={reportId} index={index} />
    </Box>
  </LeadCard>
);

export const ReportDocuments = ({ report }: { report: PartFinderReport }) => {
  const { isPartFinderDocumentsEnabled } = useFeatureFlags();
  const [activePosition, setActivePosition] = useState<number | undefined>();

  useEffect(() => {
    setActivePosition(undefined);
  }, [report.id]);

  if (!isPartFinderDocumentsEnabled) {
    return <></>;
  }

  const documents = report.documents ?? [];
  const supersedes = report.nsnReport?.supersedes ?? [];
  const previewableDocuments: PreviewableDocument[] = documents
    .map((lead, index) => ({ lead, index }))
    .filter(({ lead }) => lead.hasPreview);
  const positionOf = (index: number) => previewableDocuments.findIndex(previewable => previewable.index === index);

  return (
    <CollapsibleSection icon={<DescriptionOutlined />} title='Corroborating documents'>
      {documents.length === 0 && <BodyText color='text.secondary'>{EMPTY_STATE}</BodyText>}
      {documents.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {supersedes.length > 0 && <BodyText color='text.secondary'>Supersedes {supersedes.join(', ')}</BodyText>}
          {documents.map((lead, index) => (
            <DocumentLeadCard
              key={lead.url}
              lead={lead}
              reportId={report.id}
              index={index}
              onPreviewClick={() => setActivePosition(positionOf(index))}
            />
          ))}
        </Box>
      )}
      {activePosition !== undefined && activePosition >= 0 && previewableDocuments[activePosition] !== undefined && (
        <DocumentPreviewLightbox
          reportId={report.id}
          documents={previewableDocuments}
          activePosition={activePosition}
          onNavigate={setActivePosition}
          onClose={() => setActivePosition(undefined)}
        />
      )}
    </CollapsibleSection>
  );
};
